import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { generateProcess } from 'legacy/lut/components/widgets/public/partner360/commitment/helpers/generateProcess'
import { requestRecalculateCompleteness } from 'legacy/lut/features/partner360/completeness'
import { useJbpSettings } from 'legacy/lut/features/partner360/jbpSettings'
import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { useRef, useState } from 'react'

import { useSafetyTranslation } from 'cng-common/features/translations/hooks'
import { NotificationController } from 'core-components/library/popup'
import { type AcknowledgmentSignerSelectionPayload } from 'partner360/features/acknowledgmentSignerPicker'
import { getTranslatedText } from 'partner360/helpers/getTranslatedText'
import { useHoldPlanSectionOpen } from 'partner360/lib/planSectionBusy'
import { useNetworkDebugSource } from 'shared/lib/networkDebug'

import {
  addAcknowledgmentSigner,
  changeAcknowledgmentSigner,
  dropAcknowledgment,
  refuseAcknowledgment,
  removeAcknowledgmentSigner,
  resendAcknowledgment,
  selectAcknowledgmentSigner,
  signOffAcknowledgment,
  toAcknowledgmentEntryKey,
} from '../api/acknowledgmentActions'
import type { AcknowledgmentPartnerInfo } from '../api/fetchAcknowledgmentPartnerInfo'
import { upsertPbmManagerConfirmation } from '../api/upsertPbmManagerConfirmation'
import { getSignerNameParts } from '../lib/getSignerNameParts'
import { ACKNOWLEDGMENT_STATUS_DIALOG_TRANSLATION_KEYS } from '../translations'

import { AcknowledgmentAction, CommitmentTypes } from './enums'
import type {
  AcknowledgmentCommitmentItem,
  AcknowledgmentOffering,
  AcknowledgmentUpdatedPayload,
} from './types'

/* The actions that change what the plan's completeness counts, as the partner page's widget
   recalculates it after them. */
const COMPLETENESS_ACTIONS = new Set<AcknowledgmentAction | 'confirmManager'>([
  AcknowledgmentAction.SignOff,
  AcknowledgmentAction.Refuse,
  AcknowledgmentAction.Drop,
  AcknowledgmentAction.Add,
  AcknowledgmentAction.Remove,
])

const PDF_FAILED_DROP_COMMENT =
  '[System error]: Failed to generate the plan PDF, therefore the commitment was cancelled.'
const PDF_FAILED_MESSAGE = 'Attachment save failed, commitment aborted, please, try again later.'

type UseAcknowledgmentActionsParams = {
  reload: () => Promise<void>
  partnerId: string
  fYear: number
  programType: AcknowledgmentOffering
  partnerInfo: AcknowledgmentPartnerInfo | null
  onSigned?: (item: AcknowledgmentCommitmentItem) => void
  onNotified?: (item: AcknowledgmentCommitmentItem) => void
  onUpdated?: (payload: AcknowledgmentUpdatedPayload) => void | Promise<void>
}

export const useAcknowledgmentActions = ({
  reload,
  partnerId,
  fYear,
  programType,
  partnerInfo,
  onSigned,
  onNotified,
  onUpdated,
}: UseAcknowledgmentActionsParams) => {
  const t = useSafetyTranslation()
  const dispatch = useAppDispatch()
  const { jbpSettings } = useJbpSettings()
  const debugSource = useNetworkDebugSource()
  const [pendingItem, setPendingItem] = useState<AcknowledgmentCommitmentItem | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const mutationInFlight = useRef(false)

  useHoldPlanSectionOpen(isSubmitting)

  const runMutation = async ({
    item,
    action,
    successMessageKey,
    operation,
    onSuccess,
  }: {
    item: AcknowledgmentCommitmentItem
    action: AcknowledgmentAction | 'confirmManager'
    successMessageKey: string
    operation: () => Promise<void>
    onSuccess?: () => void | Promise<void>
  }) => {
    if (mutationInFlight.current) return false
    mutationInFlight.current = true
    setIsSubmitting(true)

    try {
      await operation()

      if (COMPLETENESS_ACTIONS.has(action)) {
        await dispatch(requestRecalculateCompleteness({ partnerId, fYear }))
      }

      await reload()
      await onSuccess?.()
      await onUpdated?.({ action, item })
      NotificationController.showSuccess({
        message: getTranslatedText({
          t,
          key: successMessageKey,
        }),
      })

      return true
    } catch (e: any) {
      NotificationController.showError({
        message: getTranslatedText({
          t,
          key: ACKNOWLEDGMENT_STATUS_DIALOG_TRANSLATION_KEYS.notifications.requestFailed,
          replaceParams: [{ key: '{error}', value: e.message }],
        }),
      })

      return false
    } finally {
      mutationInFlight.current = false
      setIsSubmitting(false)
    }
  }

  const handleSign = async (item: AcknowledgmentCommitmentItem) => {
    return runMutation({
      item,
      action: AcknowledgmentAction.SignOff,
      successMessageKey:
        ACKNOWLEDGMENT_STATUS_DIALOG_TRANSLATION_KEYS.notifications.acknowledgmentUpdated,
      operation: async () => {
        await signOffAcknowledgment(toAcknowledgmentEntryKey(item), debugSource)

        /* The PBM's signature attaches the plan PDF; a plan that could not be attached is not
           left signed. */
        if (item.CommitmentType !== CommitmentTypes.PBM || !partnerInfo?.entryId) {
          return
        }

        const isGenerated = await generateProcess({
          partnerId: item.PartnerId,
          fyear: item.FYear,
          programType: item.ProgramType as ProgramTypes,
          generatePDFOnBackend: Boolean(jbpSettings?.generatePDFOnBackend),
          setProgressPercentage: () => {},
          partnerInfoEntryId: partnerInfo.entryId,
        })

        if (isGenerated) {
          return
        }

        await dropAcknowledgment(
          toAcknowledgmentEntryKey(item),
          PDF_FAILED_DROP_COMMENT,
          debugSource
        )
        await reload()

        throw new Error(PDF_FAILED_MESSAGE)
      },
      onSuccess: async () => {
        onSigned?.(item)
      },
    })
  }

  const handleNotify = async (item: AcknowledgmentCommitmentItem) => {
    const wasSubmitted = await runMutation({
      item,
      action: AcknowledgmentAction.Resend,
      successMessageKey:
        ACKNOWLEDGMENT_STATUS_DIALOG_TRANSLATION_KEYS.notifications.notificationSent,
      operation: async () => {
        await resendAcknowledgment(toAcknowledgmentEntryKey(item), debugSource)
      },
      onSuccess: async () => {
        onNotified?.(item)
      },
    })

    if (wasSubmitted) {
      setPendingItem(null)
    }

    return wasSubmitted
  }

  const handleAddSigner = async (
    commitmentType: CommitmentTypes,
    formData: AcknowledgmentSignerSelectionPayload
  ) => {
    const item = {
      EntryId: 0,
      PartnerId: partnerId,
      FYear: fYear,
      ProgramType: programType,
      CommitmentType: commitmentType,
    } as AcknowledgmentCommitmentItem

    return runMutation({
      item,
      action: AcknowledgmentAction.Add,
      successMessageKey: ACKNOWLEDGMENT_STATUS_DIALOG_TRANSLATION_KEYS.notifications.signerAdded,
      operation: async () => {
        await addAcknowledgmentSigner(
          { partnerId, fYear, programType, commitmentType },
          formData,
          debugSource
        )
      },
    })
  }

  const handleChangeSigner = async (
    item: AcknowledgmentCommitmentItem,
    formData: AcknowledgmentSignerSelectionPayload,
    action: AcknowledgmentAction.Change | AcknowledgmentAction.Select
  ) => {
    return runMutation({
      item,
      action,
      successMessageKey: ACKNOWLEDGMENT_STATUS_DIALOG_TRANSLATION_KEYS.notifications.signerUpdated,
      operation: async () => {
        const update =
          action === AcknowledgmentAction.Change
            ? changeAcknowledgmentSigner
            : selectAcknowledgmentSigner

        await update(
          toAcknowledgmentEntryKey(item),
          { ...formData, isOptional: item.IsOptional },
          debugSource
        )
      },
    })
  }

  /* The flag rides on a change to the same signer, as the RAM assignment's toggle sets it. */
  const handleSetOptional = async (item: AcknowledgmentCommitmentItem, isOptional: boolean) => {
    return runMutation({
      item,
      action: AcknowledgmentAction.Change,
      successMessageKey: ACKNOWLEDGMENT_STATUS_DIALOG_TRANSLATION_KEYS.notifications.signerUpdated,
      operation: async () => {
        await changeAcknowledgmentSigner(
          toAcknowledgmentEntryKey(item),
          {
            email: item.SignerEmail ?? '',
            ...getSignerNameParts(item),
            role: item.SignerRole,
            contactId: item.ContactId ? String(item.ContactId) : undefined,
            isOptional: isOptional ? 1 : 0,
          },
          debugSource
        )
      },
    })
  }

  const handleDrop = async (item: AcknowledgmentCommitmentItem, comment: string) => {
    return runMutation({
      item,
      action: AcknowledgmentAction.Drop,
      successMessageKey:
        ACKNOWLEDGMENT_STATUS_DIALOG_TRANSLATION_KEYS.notifications.signatureCanceled,
      operation: async () => {
        await dropAcknowledgment(toAcknowledgmentEntryKey(item), comment, debugSource)
      },
    })
  }

  const handleRefuse = async (item: AcknowledgmentCommitmentItem, comment: string) => {
    return runMutation({
      item,
      action: AcknowledgmentAction.Refuse,
      successMessageKey:
        ACKNOWLEDGMENT_STATUS_DIALOG_TRANSLATION_KEYS.notifications.acknowledgmentRefused,
      operation: async () => {
        await refuseAcknowledgment(toAcknowledgmentEntryKey(item), comment, debugSource)
      },
    })
  }

  const handleRemove = async (item: AcknowledgmentCommitmentItem) => {
    return runMutation({
      item,
      action: AcknowledgmentAction.Remove,
      successMessageKey: ACKNOWLEDGMENT_STATUS_DIALOG_TRANSLATION_KEYS.notifications.signerRemoved,
      operation: async () => {
        await removeAcknowledgmentSigner(toAcknowledgmentEntryKey(item), debugSource)
      },
    })
  }

  const handleConfirmManager = async (item: AcknowledgmentCommitmentItem) => {
    return runMutation({
      item,
      action: 'confirmManager',
      successMessageKey:
        ACKNOWLEDGMENT_STATUS_DIALOG_TRANSLATION_KEYS.notifications.managerConfirmed,
      operation: async () => {
        await upsertPbmManagerConfirmation(item, debugSource)
      },
    })
  }

  return {
    isSubmitting,
    pendingItem,
    setPendingItem,
    handleSign,
    handleNotify,
    handleAddSigner,
    handleChangeSigner,
    handleSetOptional,
    handleDrop,
    handleRefuse,
    handleRemove,
    handleConfirmManager,
  }
}
